import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const axiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: 15000,
    withCredentials: true,   // Send HttpOnly cookies on cross-origin requests
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add a request interceptor to attach the token to every request
axiosInstance.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Currency interceptor — only appends ?currency= to commerce-related endpoints
// to avoid polluting auth, contact, blog, and admin requests.
const CURRENCY_PATHS = ['/products', '/books', '/bundles', '/cart', '/orders', '/shipping', '/coupons', '/store-categories'];
axiosInstance.interceptors.request.use((config) => {
    const url = config.url || '';
    const isAdminPage = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');
    if (!isAdminPage && CURRENCY_PATHS.some(p => url.startsWith(p))) {
        const currency = localStorage.getItem('nls_currency') || 'USD';
        config.params = { ...config.params, currency };
    }
    return config;
});

// Prevent concurrent 401s from triggering multiple redirects or refresh loops.
let isRedirecting = false;
let isRefreshing = false;
let refreshSubscribers = [];

const onTokenRefreshed = (newToken) => {
    refreshSubscribers.forEach(cb => cb(newToken));
    refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
    refreshSubscribers.push(cb);
};

// Auth endpoints that should NOT trigger a silent refresh (expected failures).
const AUTH_PATHS = ['/auth/login', '/auth/register'];

// Add a response interceptor to handle errors globally
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // Don't retry auth endpoints, already-retried requests, or non-401 errors
        if (
            error.response?.status !== 401 ||
            AUTH_PATHS.some(p => originalRequest?.url?.includes(p)) ||
            originalRequest?.url?.includes('/auth/refresh') ||
            originalRequest?._retry
        ) {
            return Promise.reject(error);
        }

        // If already refreshing, queue this request until refresh completes
        if (isRefreshing) {
            return new Promise((resolve) => {
                addRefreshSubscriber((newToken) => {
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    resolve(axiosInstance(originalRequest));
                });
            });
        }

        isRefreshing = true;
        originalRequest._retry = true;

        try {
            // Attempt silent refresh — the refresh token is in an HttpOnly cookie
            const { data } = await axiosInstance.post('/auth/refresh');
            const newToken = data.token;
            localStorage.setItem('token', newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            onTokenRefreshed(newToken);
            return axiosInstance(originalRequest);
        } catch (refreshError) {
            // Refresh failed — force logout
            if (!isRedirecting) {
                isRedirecting = true;
                localStorage.removeItem('token');
                window.location.href = '/login';
                setTimeout(() => { isRedirecting = false; }, 3000);
            }
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);

export default axiosInstance;
